<template>
  <div>
    <vx-card class="mb-4">
      <div class="mb-2 d-flex justify-content-between align-items-start">
        <div>
          <h4 class="mb-0">{{ isEditMode ? model.name || $t('rental_space.title') : $t('catalog.space.new_title') }}</h4>
          <small class="text-muted">{{ $t('catalog.space.subtitle') }}</small>
        </div>
        <!-- Só com ingresso ligado: sem isso o painel abriria vazio para a
             maioria dos espaços, que não exige entrada validada. -->
        <b-button
          v-if="spaceId && model.ticket_enabled"
          v-permission="'tickets.grid'"
          variant="outline-primary"
          size="sm"
          @click="$router.push(`/ingressos/checkin/rental_space/${spaceId}`)">
          <feather-icon icon="CheckSquareIcon" class="mr-50"/>
          <span class="align-middle">{{ $t('catalog.ticket.panel') }}</span>
        </b-button>
      </div>

      <wizard-shell
        :steps="steps"
        :current="currentStep"
        :done-keys="doneKeys"
        :free-navigation="isEditMode"
        :busy="busy"
        @prev="goPrev"
        @next="goNext"
        @goto="goToStep">

        <!-- ============================== PASSO 1: SOBRE O ESPAÇO -->
        <div v-show="currentStep === 'sobre'">
          <validation-observer ref="validator" #default="{valid}">
            <b-form @submit.prevent>
              <div class="vx-row">
                <div class="vx-col sm:w-7/12 w-full">
                  <validation-provider
                    #default="validationContext"
                    :name="$t('rental_space.name')"
                    rules="required"
                    mode="lazy"
                    slim>
                    <b-form-group :label="$t('rental_space.name')" label-for="name">
                      <b-form-input
                        id="name"
                        type="text"
                        class="required"
                        v-model="model.name"
                        :placeholder="$t('catalog.space.name_placeholder')"
                        :state="getValidationState(validationContext)"/>
                      <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </div>
                <div class="vx-col sm:w-5/12 w-full">
                  <validation-provider
                    #default="validationContext"
                    :name="$t('rental_space.category')"
                    rules="required"
                    slim>
                    <b-form-group :label="$t('rental_space.category')" label-for="category_id">
                      <v-select
                        id="category_id"
                        v-model="category"
                        :options="categories"
                        label="name"
                        class="required-select"
                        :required="true">
                        <div slot="no-options">Nenhum registro encontrado!</div>
                      </v-select>
                      <small class="text-danger">{{ validationContext.errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </div>
              </div>

              <validation-provider
                #default="validationContext"
                name="Estabelecimento"
                rules="required"
                slim>
                <select-suggestion
                  id="legal_entity_id"
                  column="company_name,trading_name,cnpj"
                  model="LegalEntity"
                  v-model="legalEntity"
                  :label="$t('responsible_entity')"
                  :appendClearOption="true"
                  placeholderText="Digite o nome do estabelecimento"
                  :itemTextFormat="legalEntityItemTextFormat"
                  :required="true"/>
                <small class="text-danger">{{ validationContext.errors[0] }}</small>
              </validation-provider>

              <validation-provider
                #default="validationContext"
                :name="$t('rental_space.description')"
                rules="max:500"
                slim>
                <b-form-group :label="$t('rental_space.description')" label-for="description" class="mt-1">
                  <b-form-textarea
                    id="description"
                    v-model="model.description"
                    rows="3"
                    max-rows="6"
                    :state="getValidationState(validationContext)"/>
                  <small class="text-muted d-block">{{ $t('catalog.space.description_hint') }}</small>
                  <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>

              <!-- Ingresso: opcional, e por isso fecha o passo em vez de abri-lo. -->
              <hr class="my-2"/>
              <b-form-row>
                <b-col cols="12">
                  <b-form-checkbox v-model="model.ticket_enabled" switch>
                    <strong>{{ $t('catalog.ticket.enable') }}</strong>
                  </b-form-checkbox>
                  <small class="text-muted d-block">{{ $t('catalog.ticket.enable_hint') }}</small>
                </b-col>
                <b-col v-if="model.ticket_enabled" cols="12" class="mt-2">
                  <b-form-group :label="$t('catalog.ticket.type')">
                    <choice-cards
                      :options="ticketTypeCards"
                      :value="model.ticket_type"
                      @input="value => { model.ticket_type = value }"/>
                    <small v-if="!model.ticket_type" class="text-warning d-block mt-1">{{ $t('catalog.ticket.type_required') }}</small>
                  </b-form-group>
                </b-col>
              </b-form-row>

              <div v-if="!valid" class="mt-1">
                <small class="text-warning">{{ $t('catalog.wizard.fill_required') }}</small>
              </div>
            </b-form>
          </validation-observer>
        </div>

        <!-- ============================== PASSO 2: HORÁRIOS DE FUNCIONAMENTO -->
        <div v-show="currentStep === 'horarios'">
          <template v-if="spaceId">
            <small class="text-muted d-block mb-1">{{ $t('catalog.space.schedule_hint') }}</small>
            <schedule-manager
              v-model="scheduleData"
              @change="onScheduleChange"
              @validity-change="onScheduleValidityChange"
              :readonly="false"/>
            <div class="flex justify-end items-center gap-2 mt-2" v-if="scheduleDirty && scheduleValid">
              <span class="text-warning">{{ $t('schedule.space.unsaved') }}</span>
              <b-button variant="primary" size="sm" @click="saveSchedule">
                {{ $t('catalog.space.save_schedule') }}
              </b-button>
            </div>
          </template>
        </div>

        <!-- ============================== PASSO 3: PREÇO E FOTO -->
        <div v-show="currentStep === 'venda'">
          <b-alert :show="productLoadFailed" variant="warning" class="p-1 mb-2">
            <small>{{ $t('catalog.sale.sale_unavailable') }}</small>
          </b-alert>
          <sale-step
            v-if="productExists"
            :product-id="product.id"
            :price.sync="product.price"
            :description.sync="product.description"
            :how-works.sync="product.how_works"
            :guidelines.sync="product.guidelines"
            :files="productFiles"
            :multi-sale="productMultiSale"
            :show-photos="false"
            :price-hint="$t('catalog.space.price_hint')"
            @file-uploaded="onProductFileUploaded"
            @delete-file="deleteProductFile">
            <!-- A foto do espaço é o logo dele (coleção própria, uma imagem):
                 é ela que aparece no card e na página pública do espaço. -->
            <div class="sale-step-extra">
              <h6 class="mb-50">
                <feather-icon icon="ImageIcon" class="mr-50" svgClasses="w-4 h-4"/>
                {{ $t('catalog.space.logo_title') }}
              </h6>
              <image-upload
                class="border-faint-grey border-solid border p-2 rounded-lg inline-block"
                maxHeight="120px"
                maxWidth="240px"
                :hint="$t('catalog.space.logo_hint')"
                v-model="imageData"
                :key="logoUploadURL"
                :uploadUrl="logoUploadURL"
                @upload-complete="uploadLogoSuccess"/>
            </div>
          </sale-step>
        </div>

        <!-- ============================== PASSO 4: REVISAR E PUBLICAR -->
        <div v-show="currentStep === 'revisar'">
          <review-step
            :summary="reviewSummary"
            :price="product.price"
            :price-suffix="$t('catalog.space.price_suffix')"
            :published="productPublished"
            :blockers="publishBlockers"
            :front-url="publishedFrontUrl"
            :busy="busy"
            price-step-key="venda"
            @goto="goToStep"
            @publish="publish"
            @save-draft="saveDraft"
            @unpublish="unpublish"/>
        </div>
      </wizard-shell>
    </vx-card>
  </div>
</template>

<script>
import {
  BAlert,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BButton,
  BFormInvalidFeedback,
  // Só ToastPlugin/ModalPlugin são globais (main.ts) — todo componente do
  // bootstrap-vue usado no template precisa ser importado e registrado aqui.
  BFormRow,
  BCol,
  BFormCheckbox,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, min, max } from '@validations'
import { mapGetters } from 'vuex'
import vSelect from 'vue-select'

import ImageUpload from '@/components/ImageUpload.vue'
import SelectSuggestion from '@/components/SelectSuggestion.vue'
import ScheduleManager from '@/components/ScheduleManager.vue'
import WizardShell from '@/components/catalog/WizardShell.vue'
import SaleStep from '@/components/catalog/SaleStep.vue'
import ReviewStep from '@/components/catalog/ReviewStep.vue'
import ChoiceCards from '@/components/catalog/ChoiceCards.vue'
import productBinding from '@/components/catalog/productBinding'
import { marketplaceItemUrl } from '@/components/catalog/catalog'
import { TICKET_TYPE_CARDS, ticketPayload } from '@/components/catalog/ticketTypes'

import RentalSpaceService from '@/services/api/RentalSpaceService'

const STEP_KEYS = ['sobre', 'horarios', 'venda', 'revisar']

/**
 * Cadastro de espaço de locação em passo a passo, com o produto embutido.
 *
 * A foto do anúncio do espaço é o logo do próprio espaço (coleção `logos`,
 * singleFile): é ela que o card e a página pública usam. Por isso o passo
 * "Preço e fotos" mostra o upload do logo e esconde as fotos de produto —
 * duas caixas de foto para a mesma vitrine só confundiam.
 *
 * Sem faixa de horário o espaço não aceita reserva nenhuma (a disponibilidade
 * pública devolve vazio em silêncio) — daí a grade ser passo obrigatório e
 * bloquear a publicação quando vazia.
 */
export default {
  mixins: [productBinding],
  components: {
    BAlert,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,
    BFormInvalidFeedback,
    BFormRow,
    BCol,
    BFormCheckbox,
    ValidationObserver,
    ValidationProvider,
    ImageUpload,
    vSelect,
    SelectSuggestion,
    ScheduleManager,
    WizardShell,
    SaleStep,
    ReviewStep,
    ChoiceCards,
  },

  props: {
    id: {
      type: [Number, String],
      default: null,
    },
  },

  data: () => ({
    service: null,
    currentStep: 'sobre',
    busy: false,
    /** Id do espaço recém-criado, antes de a prop refletir o replace create→edit. */
    internalId: null,
    model: {
      name: '',
      description: '',
      ticket_enabled: false,
      ticket_type: null,
    },
    category: null,
    categories: [],
    legalEntity: null,
    imageData: '',
    scheduleData: [],
    scheduleValid: true,
    // Assinatura do que está publicado, para saber se há alteração pendente.
    // A referência é a primeira emissão do ScheduleManager depois de carregar,
    // e não o payload cru do backend: o componente normaliza o formato antigo.
    scheduleSaved: '[]',
    scheduleBaselinePending: false,
  }),

  computed: {
    ...mapGetters('dataAccess', ['activeEntity', 'entities', 'loaded']),
    spaceId() {
      if (this.internalId) return this.internalId
      return this.id === null || this.id === undefined || this.id === '' ? null : Number(this.id)
    },
    isEditMode() {
      return this.spaceId !== null
    },
    scheduleDirty() {
      return JSON.stringify(this.scheduleData) !== this.scheduleSaved
    },
    scheduleCount() {
      return Array.isArray(this.scheduleData) ? this.scheduleData.length : 0
    },
    logoUploadURL() {
      const base = `${process.env.VUE_APP_API_BASE_URL}/api/v1`
      return `${base}/rental_spaces/${this.spaceId}/logo`
    },
    /** Compartilhado com o wizard de aula — ver components/catalog/ticketTypes. */
    ticketTypeCards() {
      return TICKET_TYPE_CARDS
    },
    steps() {
      return [
        { key: 'sobre', title: this.$t('catalog.space.step_about'), hint: this.$t('catalog.space.step_about_hint') },
        { key: 'horarios', title: this.$t('catalog.space.step_schedule'), hint: this.$t('catalog.space.step_schedule_hint') },
        { key: 'venda', title: this.$t('catalog.sale.step_title'), hint: this.$t('catalog.sale.step_hint') },
        { key: 'revisar', title: this.$t('catalog.review.step_title'), hint: this.$t('catalog.review.step_hint') },
      ]
    },
    doneKeys() {
      const done = []
      if (this.spaceId) done.push('sobre', 'horarios')
      if (this.productExists) done.push('venda')
      return done
    },
    publishBlockers() {
      const blockers = []
      if (!this.productExists) {
        blockers.push({ text: this.$t('catalog.lesson.blocker_sale_step'), stepKey: 'venda' })
      }
      if (this.productExists && (this.product.price === null || this.product.price === '')) {
        blockers.push({ text: this.$t('catalog.space.blocker_price'), stepKey: 'venda' })
      }
      if (this.scheduleCount === 0) {
        blockers.push({ text: this.$t('catalog.space.blocker_schedule'), stepKey: 'horarios' })
      }
      if (this.productLoadFailed) {
        blockers.push({ text: this.$t('catalog.sale.sale_unavailable') })
      }
      return blockers
    },
    publishedFrontUrl() {
      return this.productPublished ? marketplaceItemUrl('RSP', this.product.id) : null
    },
    reviewSummary() {
      return [
        {
          key: 'sobre',
          title: this.$t('catalog.space.step_about'),
          icon: 'HomeIcon',
          stepKey: 'sobre',
          rows: [
            { label: this.$t('rental_space.name'), value: this.model.name },
            { label: this.$t('catalog.space.review_category'), value: this.category && this.category.name },
            { label: this.$t('rental_space.description'), value: this.model.description },
          ],
        },
        {
          key: 'horarios',
          title: this.$t('catalog.space.step_schedule'),
          icon: 'ClockIcon',
          stepKey: 'horarios',
          rows: [{
            label: this.$t('catalog.space.review_schedules'),
            value: this.scheduleCount,
            missing: this.scheduleCount === 0,
          }],
        },
        {
          key: 'foto',
          title: this.$t('catalog.space.logo_title'),
          icon: 'ImageIcon',
          stepKey: 'venda',
          rows: [{
            label: this.$t('catalog.space.logo_title'),
            value: this.imageData ? 'Enviada' : this.$t('catalog.lesson.review_photos_none'),
          }],
        },
      ]
    },
  },

  watch: {
    '$route.query.step'(value) {
      const step = STEP_KEYS.includes(value) ? value : 'sobre'
      if (step !== this.currentStep) {
        this.enterStep(step)
      }
    },
  },

  methods: {
    legalEntityItemTextFormat(model) {
      const trading = model?.trading_name || ''
      const company = model?.company_name || ''
      const label = trading && company ? `${trading} - ${company}` : (trading || company)
      return label || model?.name || `ID: ${model?.id}`
    },
    prefillLegalEntity() {
      if (this.legalEntity || this.spaceId) {
        return
      }
      if (this.activeEntity) {
        this.legalEntity = { id: this.activeEntity.id, trading_name: this.activeEntity.name || '', company_name: '' }
        return
      }
      if (Array.isArray(this.entities) && this.entities.length === 1) {
        const [entity] = this.entities
        this.legalEntity = { id: entity.id, trading_name: entity.trading_name || entity.name || '', company_name: entity.company_name || '' }
      }
    },
    getValidationState({ invalid, validated }) {
      const valid = !invalid
      return !validated ? null : valid
    },

    // ------------------------------------------------------------------
    // Navegação
    // ------------------------------------------------------------------
    stepIndex(key) {
      return STEP_KEYS.indexOf(key)
    },
    syncStepQuery(step) {
      if (this.$route.query.step !== step) {
        this.$router.replace({ query: { ...this.$route.query, step } }).catch(() => {})
      }
    },
    async goNext() {
      const index = this.stepIndex(this.currentStep)
      const next = STEP_KEYS[index + 1]
      if (!next) return
      const left = await this.leaveStep(this.currentStep)
      if (!left) return
      await this.enterStep(next)
    },
    goPrev() {
      const index = this.stepIndex(this.currentStep)
      const prev = STEP_KEYS[index - 1]
      if (prev) this.goToStep(prev)
    },
    async goToStep(step) {
      if (step === this.currentStep) return
      const left = await this.leaveStep(this.currentStep)
      if (!left) return
      await this.enterStep(step)
    },
    async leaveStep(step) {
      if (step === 'sobre') {
        const valid = await this.$refs.validator.validate()
        if (!valid) {
          this.notifyWarning(this.$vs, this.$t('catalog.wizard.fill_required'))
          return false
        }
        return this.saveSpaceCore()
      }
      if (step === 'horarios') {
        if (!this.scheduleValid) {
          this.notifyWarning(this.$vs, this.$t('catalog.lesson.grid_fix_errors'))
          return false
        }
        if (this.scheduleDirty) {
          return this.saveSchedule()
        }
        return true
      }
      return true
    },
    async enterStep(step) {
      if (!this.spaceId && step !== 'sobre') {
        step = 'sobre'
      }
      // Reentrada (watcher da query + goNext chegam juntos no create→edit).
      if (step === this.currentStep && this.$route.query.step === step) {
        return
      }
      if (step === 'venda' && this.spaceId) {
        const ready = await this.ensureSale()
        if (!ready) return
      }
      if (step === 'revisar' && this.spaceId && !this.productExists && !this.productLoadFailed) {
        await this.ensureSale()
      }
      this.currentStep = step
      this.syncStepQuery(step)
    },

    // ------------------------------------------------------------------
    // Persistência
    // ------------------------------------------------------------------
    async saveSpaceCore() {
      this.busy = true
      this.$vs.loading()
      try {
        const wasCreate = !this.spaceId
        const data = await this.service.createOrUpdate({
          id: this.spaceId,
          name: this.model.name,
          description: this.model.description,
          category_id: this.category ? this.category.id : null,
          legal_entity_id: this.legalEntity ? this.legalEntity.id : null,
          // O payload do espaço é montado em DOIS lugares (saveSpaceCore e
          // persistAll). Faltar aqui faria "Publicar agora" apagar o ingresso que
          // o passo Sobre acabou de salvar.
          ...ticketPayload(this.model),
        })
        if (wasCreate) {
          this.internalId = data.id
          // Aguardado: navegação dupla sob o guard assíncrono cancela as duas
          // (ver saveLessonCore do formulário de aula).
          await this.$router.replace(`/rental_spaces/${data.id}/edit?step=horarios`).catch(() => {})
        }
        return true
      } catch (error) {
        this.showErrors(this.$refs.validator, error, this)
        return false
      } finally {
        this.$vs.loading.close()
        this.busy = false
      }
    },
    async saveSchedule() {
      if (!this.scheduleValid) return false
      this.busy = true
      this.$vs.loading()
      try {
        const payload = [...this.scheduleData]
        await this.service.updateSchedule(this.spaceId, { scheduleData: payload })
        this.scheduleSaved = JSON.stringify(payload)
        this.notifySuccess(this.$vs, this.$t('messages.success'))
        return true
      } catch (error) {
        this.notifyErrors(this.$vs, error)
        return false
      } finally {
        this.$vs.loading.close()
        this.busy = false
      }
    },
    async ensureSale() {
      if (this.productExists || !this.spaceId || !this.legalEntity) {
        return this.productExists
      }
      this.busy = true
      this.$vs.loading()
      try {
        await this.ensureProduct({
          itemType: 'RSP',
          itemId: this.spaceId,
          itemName: this.model.name,
          legalEntityId: this.legalEntity.id,
        })
        this.productLoadFailed = false
        return true
      } catch (error) {
        // 'sale-unavailable': recusa deliberada de criar anúncio às cegas
        // depois de uma leitura falha (evita duplicar o anúncio existente).
        if (!error || error.message !== 'sale-unavailable') {
          this.notifyErrors(this.$vs, error)
        }
        this.productLoadFailed = true
        return false
      } finally {
        this.$vs.loading.close()
        this.busy = false
      }
    },

    // ------------------------------------------------------------------
    // Publicação
    // ------------------------------------------------------------------
    async publish() {
      if (this.productNeedsPriceConfirmation()) {
        this.confirmProductPriceChange(() => this.publish())
        return
      }
      await this.persistAll('RE', this.$t('catalog.review.published_success'))
    },
    async saveDraft() {
      const status = this.product.status && this.product.status !== 'RE' ? this.product.status : 'CR'
      await this.persistAll(status, this.$t('catalog.review.draft_saved'))
    },
    async unpublish() {
      await this.persistAll('DI', this.$t('catalog.review.unpublished_success'))
    },
    async persistAll(status, successMessage) {
      this.busy = true
      this.$vs.loading()
      try {
        await this.service.createOrUpdate({
          id: this.spaceId,
          name: this.model.name,
          description: this.model.description,
          category_id: this.category ? this.category.id : null,
          legal_entity_id: this.legalEntity ? this.legalEntity.id : null,
          // O payload do espaço é montado em DOIS lugares (saveSpaceCore e
          // persistAll). Faltar aqui faria "Publicar agora" apagar o ingresso que
          // o passo Sobre acabou de salvar.
          ...ticketPayload(this.model),
        })
        await this.saveProductAs(status, {
          itemName: this.model.name,
          legalEntityId: this.legalEntity ? this.legalEntity.id : null,
        })
        this.notifySuccess(this.$vs, successMessage)
      } catch (error) {
        this.notifyErrors(this.$vs, error)
      } finally {
        this.$vs.loading.close()
        this.busy = false
      }
    },

    // ------------------------------------------------------------------
    // Grade / carga
    // ------------------------------------------------------------------
    onScheduleChange(scheduleData) {
      this.scheduleData = [...scheduleData]
      // Primeira emissão depois do load: é ela que define o "já publicado".
      if (this.scheduleBaselinePending) {
        this.scheduleBaselinePending = false
        this.scheduleSaved = JSON.stringify(this.scheduleData)
      }
    },
    onScheduleValidityChange(valid) {
      this.scheduleValid = valid
    },
    uploadLogoSuccess() {
      this.notifySuccess(this.$vs, this.$t('messages.success'), 5000)
    },
    async loadData(id) {
      this.$vs.loading()
      try {
        const response = await this.service.read(id)
        this.model.name = response.name
        this.model.description = response.description
        this.model.ticket_enabled = !!response.ticket_enabled
        this.model.ticket_type = response.ticket_type || null
        this.category = response.category
        this.legalEntity = response.legal_entity
        this.imageData = this._.get(response, 'logo_url')
        this.scheduleBaselinePending = true
        this.scheduleData = Array.isArray(response.schedule_weekdays)
          ? response.schedule_weekdays
          : []
      } finally {
        this.$vs.loading.close()
      }
    },
  },

  beforeRouteLeave(to, from, next) {
    const stayingInForm = (to.name === 'rental-space-edit' || to.name === 'rental-space-create')
      && String(to.params.id || '') === String(this.spaceId || '')
    const priceDirty = this.productExists
      && this.productOriginalPrice !== null
      && Number(this.product.price) !== Number(this.productOriginalPrice)
    if (!stayingInForm && (priceDirty || this.scheduleDirty)) {
      if (!window.confirm(this.$t('catalog.wizard.unsaved_leave_confirm'))) {
        next(false)
        return
      }
    }
    next()
  },

  beforeMount() {
    this.service = RentalSpaceService.build(this.$vs)
  },

  async mounted() {
    if (!this.loaded) {
      await this.$store.dispatch('dataAccess/setupFromLogin')
    }
    this.prefillLegalEntity()

    this.service.findAllCategories().then(data => {
      this.categories = data
    })

    if (this.spaceId) {
      await this.loadData(this.spaceId)
      await this.loadLinkedProduct('RSP', this.spaceId, this.model.name)
    }

    const requested = this.$route.query.step
    const initial = STEP_KEYS.includes(requested) ? requested : 'sobre'
    await this.enterStep(initial)
  },
}
</script>

<style lang="scss" scoped>
.sale-step-extra {
  padding-top: 1rem;
}
</style>
